import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Alert } from "react-bootstrap";

export type AlertVariant =
    | "primary" | "secondary" | "success" | "danger"
    | "warning" | "info" | "light" | "dark";

export type AlertOptions = {
  id?: string;
  variant?: AlertVariant;
  dismissible?: boolean;
  autoHideMs?: number;
  content: React.ReactNode;
};

type AlertItem = Required<Pick<AlertOptions, "id">> & AlertOptions;

type AlertContextValue = {
  showAlert: (opts: AlertOptions) => string;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
};

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

export const useAlerts = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error("useAlerts must be used within <AlertProvider/>");
  return ctx;
};

type AlertProviderProps = {
  children: React.ReactNode;
  positionClassName?: string;
};

export const AlertProvider: React.FC<AlertProviderProps> = ({
                                                              children,
                                                              positionClassName = ""
                                                            }) => {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  const removeAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  const showAlert = useCallback((opts: AlertOptions) => {
    const id = opts.id ?? Math.random().toString(36).slice(2);
    const alert: AlertItem = {
      id,
      variant: opts.variant ?? "success",
      dismissible: opts.dismissible ?? true,
      autoHideMs: opts.autoHideMs,
      content: opts.content,
    };
    setAlerts(prev => [...prev, alert]);
    if (alert.autoHideMs && alert.autoHideMs > 0) {
      window.setTimeout(() => removeAlert(id), alert.autoHideMs);
    }
    return id;
  }, [removeAlert]);

  const clearAlerts = useCallback(() => setAlerts([]), []);

  const value = useMemo(() => ({ showAlert, removeAlert, clearAlerts }), [showAlert, removeAlert, clearAlerts]);

  return (
      <AlertContext.Provider value={value}>
        {children}

        <div
            className={`position-fixed w-100 d-flex flex-column align-items-center gap-2 p-3 ${positionClassName}`}
            style={{ top: 0, left: 0, zIndex: 1080, pointerEvents: "none" }}
            aria-live="polite"
            aria-atomic="true"
        >
          {alerts.map(a => (
              <div key={a.id} style={{ width: "min(720px, 92vw)", pointerEvents: "auto" }}>
                <Alert
                    variant={a.variant}
                    dismissible={a.dismissible}
                    onClose={() => removeAlert(a.id)}
                    className="shadow page-alert rounded-3"
                >
                  {a.content}
                </Alert>
              </div>
          ))}
        </div>
      </AlertContext.Provider>
  );
};
